import React from 'react';
import { formatNumber } from '../format';

// Backend status -> the pill the design shows.
const PILLS = {
    running: { className: 'running', label: 'Running' },
    ready_for_review: { className: 'awaiting', label: 'Awaiting approval' },
    completed: { className: 'applied', label: 'Applied' },
    failed: { className: 'failed', label: 'Failed' },
};

/**
 * Rows the import actually kept.
 *
 * Not total_found, which counts every parcel in the ZIP whether or not the land
 * use filter let it through. Labelling that "rows" put 3,477 next to a queue
 * holding 509 of them.
 */
function queued(record) {
    return (record.new_count || 0) + (record.matched_count || 0);
}

function statsFor(record) {
    if (record.status === 'running') {
        return [
            { key: 'scanned', value: formatNumber(record.scanned_count) || '0' },
            { key: 'new', value: formatNumber(record.new_count) || '0' },
            { key: 'already in', value: formatNumber(record.matched_count) || '0' },
        ];
    }

    if (record.status === 'completed') {
        return [
            { key: 'imported', value: formatNumber(record.imported_count) || '0' },
            { key: 'rows', value: formatNumber(queued(record)) || '0' },
            { key: 'pending', value: formatNumber(record.pending_count) || '0' },
        ];
    }

    return [
        { key: 'rows', value: formatNumber(queued(record)) || '0' },
        { key: 'new', value: formatNumber(record.new_count) || '0' },
        { key: 'already in', value: formatNumber(record.matched_count) || '0' },
    ];
}

/** Owners Kore may already hold, which approving leaves in the queue until someone chooses. */
function withReview(stats, record) {
    if (!(record.review_count > 0)) return stats;

    return stats.concat({ key: 'need review', value: formatNumber(record.review_count), tone: 'warn' });
}

/**
 * ZIP imports: what is running, what is waiting to be approved, and the box
 * that starts another one.
 */
export default function ImportsStep({ imports, isLoading, zip, isStarting, error, onZip, onStart, onQueue, onLog }) {
    return (
        <div className="Imports">
            {isLoading && <div className="SidePanel-loading">Loading imports…</div>}

            {!isLoading && imports.length === 0 && (
                <div className="SidePanel-empty">No imports yet.<br />Queue a ZIP below to start one.</div>
            )}

            {imports.map((record) => {
                const pill = PILLS[record.status] || PILLS.running;
                const reviewable = record.status === 'ready_for_review' && record.pending_count > 0;

                return (
                    <div key={record.id} className="Import-card">
                        <div className="Import-head">
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="Import-title">ZIP {record.zip}</div>
                                <div className="Import-meta">
                                    {record.error || [record.requested_by, record.created_at && new Date(record.created_at).toLocaleString()]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </div>
                            </div>
                            <div className={'Import-pill Import-pill--' + pill.className}>{pill.label}</div>
                        </div>

                        <div className="Import-progress">
                            <div className="Import-bar">
                                <div
                                    className={'Import-bar-fill Import-bar-fill--' + pill.className}
                                    style={{ width: record.progress + '%' }}
                                />
                            </div>
                            <div className="Import-pct">{record.progress}%</div>
                        </div>

                        <div className="Import-stats">
                            {withReview(statsFor(record), record).map((stat) => (
                                <div key={stat.key}>
                                    <div className={'Import-stat-value' + (stat.tone ? ' Import-stat-value--' + stat.tone : '')}>
                                        {stat.value}
                                    </div>
                                    <div className="Import-stat-key">{stat.key}</div>
                                </div>
                            ))}
                        </div>

                        <div className="Import-actions">
                            {reviewable && (
                                <button type="button" className="Btn Btn--sm Btn--primary" onClick={() => onQueue(record)}>
                                    Review queue
                                </button>
                            )}
                            <button type="button" className="Btn Btn--sm" onClick={() => onLog(record)}>Log</button>
                        </div>
                    </div>
                );
            })}

            <div className="Import-new">
                <div className="Import-new-title">New import</div>
                <div className="Import-new-row">
                    <input
                        className="Import-new-input"
                        placeholder="ZIP code, e.g. 75023"
                        value={zip}
                        onChange={(event) => onZip(event.target.value)}
                        onKeyPress={(event) => { if (event.key === 'Enter') onStart(); }}
                    />
                    <button
                        type="button"
                        className="Btn Btn--primary"
                        onClick={onStart}
                        disabled={isStarting || !/^\d{5}$/.test(zip)}
                    >
                        {isStarting ? 'Queuing…' : 'Queue'}
                    </button>
                </div>
                {error && <div className="Sel-help">{error}</div>}
            </div>
        </div>
    );
}
